import { ref, computed, watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { calculateRefund } from '@/lib/refunds/remote'
import { calculateRefundBulk } from '@/lib/refunds/remote'
import { TAX_REBATE_MARRIED, TAX_REBATE_SINGLE } from '@/lib/utils/calculate-pension'
// import type { Form11 } from '@/types/form11'; // Assuming Form11 type exists

// Placeholder type - replace with actual import if available
interface Form11 {
  id: string | number
  year: number
  status: string
  snapshot?: Record<string, any>
  created_at: string | Date
  // Add other relevant fields from your actual Form11 type
}

interface ProcessedForm extends Form11 {
  totalApprovedRefund: number
  snapshot: Record<string, any>
  /** Individual refund amounts per category for easy display */
  categoryRefunds: Record<string, number>
}


const { user } = useAuth()

// Helper function to format PascalCase to Capitalized Case
const formatCategoryName = (pascalCase: string): string => {
  if (!pascalCase) return ''
  return pascalCase
    .replace(/([A-Z])/g, ' $1') // Insert a space before all caps
    .trim() // Remove leading space
}

/**
 * Forms enriched with the total refund amount approved for that filing.
 * We calculate the amount on the server (preferred) and gracefully fall back
 * to the former in-browser calculation if the API fails for any reason.
 */
const processedForms = ref<ProcessedForm[]>([])

async function recalculate(): Promise<void> {
  const forms = (user.value?.form11 || []) as Form11[]

  // Derive user marginal rate using the same logic as calculate-pension.ts
  const annualIncome = user.value?.annual_income ?? 0
  const maritalStatus = (user.value?.marital_status ?? 'single') as string
  const taxRebate =
    maritalStatus === 'married' ? TAX_REBATE_MARRIED : TAX_REBATE_SINGLE
  const marginalRate = annualIncome > taxRebate ? 0.4 : 0.2

  /**
   * ---------------------------------------------------------------------
   * 1. Group all forms by tax year so we can call the bulk-API only once
   *    per unique year. This drastically reduces network chatter when a
   *    user has many filings for the same year.
   * ---------------------------------------------------------------------
   */
  const formsByYear = new Map<number, Form11[]>()
  for (const form of forms) {
    const list = formsByYear.get(form.year) || []
    list.push(form)
    formsByYear.set(form.year, list)
  }

  /**
   * ---------------------------------------------------------------------
   * 2. For every year build an `items` payload that preserves duplicates.
   *    While building the list we capture metadata so we can route the
   *    amounts returned by the backend back to the originating form &
   *    category.
   */
  const amountsByYearByForm: Record<number, Record<string, Record<string, number>>> = {}

  await Promise.all(
    Array.from(formsByYear.entries()).map(async ([year, yearForms]) => {
      type Meta = { formIdx: number; category: string }
      const items: Array<{ category: string; data?: Record<string, any> }> = []
      const meta: Meta[] = []

      yearForms.forEach((form, formIdx) => {
        const snapshot = form.snapshot || {}
        Object.entries(snapshot).forEach(([categoryName, categoryData]) => {
          items.push({
            category: categoryName,
            data: { ...(categoryData || {}), marginalRate },
          })
          meta.push({ formIdx, category: categoryName })
        })
      })

      // Initialise nested map for the year
      const yearResult: Record<string, Record<string, number>> = {}
      amountsByYearByForm[year] = yearResult

      // Helper to record amount in nested structure
      const recordAmount = (formIdx: number, category: string, amount: number) => {
        const formKey = String(yearForms[formIdx].id)
        if (!yearResult[formKey]) yearResult[formKey] = {}
        yearResult[formKey][category] = amount
      }

      try {
        const result = await calculateRefundBulk(year, items)

        /**
         * calculateRefundBulk now returns an array preserving original order
         * so we can map 1-to-1 via the `meta` list. Fall back to previous
         * object-map behaviour for backward compatibility.
         */
        if (Array.isArray(result)) {
          result.forEach((amount, idx) => {
            const { formIdx, category } = meta[idx]
            recordAmount(formIdx, category, Number(amount) || 0)
          })
        } else {
          // Older API – single number per unique category; use it for every
          // occurrence of that category.
          meta.forEach(({ formIdx, category }) => {
            const amount = Number((result as Record<string, number>)[category]) || 0
            recordAmount(formIdx, category, amount)
          })
        }
      } catch (error) {
        console.warn(`Bulk refund calculation failed for year ${year}`, error)
        // Fallback: individual requests per item (still serialised per year)
        await Promise.all(
          items.map(async (item, idx) => {
            const amount = await calculateRefund(year, item.category, item.data)
            const { formIdx, category } = meta[idx]
            recordAmount(formIdx, category, amount)
          }),
        )
      }
    }),
  )

  /**
   * ---------------------------------------------------------------------
   * 3. Enrich the original forms with the calculated amounts. Even though
   *    the refund amounts were fetched per year, we still need the totals
   *    per *form* so that the UI can display them as before.
   * ---------------------------------------------------------------------
   */
  const enriched: ProcessedForm[] = forms.map((form) => {
    const snapshotDetails: Record<string, any> = form.snapshot || {}
    const categoryRefunds: Record<string, number> = {}
    let totalApprovedRefund = 0

    const yearFormAmounts = amountsByYearByForm[form.year]?.[String(form.id)] || {}

    for (const categoryName of Object.keys(snapshotDetails)) {
      const amount = Number(yearFormAmounts[categoryName]) || 0
      categoryRefunds[categoryName] = amount
      totalApprovedRefund += amount
    }

    return {
      ...form,
      snapshot: snapshotDetails,
      categoryRefunds,
      totalApprovedRefund,
    }
  })

  // Newest year first
  processedForms.value = enriched.sort((a, b) => b.year - a.year)
}

// Re-run whenever the user payload changes (incl. on first run).
watch(
  () => user.value?.form11,
  () => {
    if (user.value) {
      recalculate()
    }
  },
  { immediate: true, deep: true },
)

const totalTaxbackCredits = computed(() =>
  processedForms.value.reduce((acc, form) => acc + form.totalApprovedRefund, 0),
)

export function useTaxbackCredits() {
  return {
    processedForms,
    totalTaxbackCredits,
    formatCategoryName,
    // Expose helper for one-off calls if a component needs to trigger a fresh calculation.
    calculateRefund,
  }
}
